import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { compactMoney, money } from '../../lib/format';
import { sumBy, useTenantScope } from '../../store/selectors';
import { SectionCard } from '../../ui/primitives';

export default function FundValueChart() {
  const scope = useTenantScope();
  const data = useMemo(
    () =>
      scope.funds
        .map((fund) => {
          const holdings = scope.holdings.filter((holding) => holding.fundId === fund.id);
          return {
            code: fund.code,
            name: fund.name,
            value: Math.round(sumBy(holdings, (holding) => holding.value)),
            holders: new Set(holdings.map((holding) => holding.holderId)).size,
          };
        })
        .filter((row) => row.value > 0)
        .sort((left, right) => right.value - left.value),
    [scope],
  );
  return (
    <SectionCard
      title="Value on register by fund"
      subtitle="Units held × current unit price, from the ledger"
    >
      <Box sx={{ height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 8, right: 24, top: 4, bottom: 4 }}
          >
            <CartesianGrid horizontal={false} stroke="#E2E8F0" />
            <XAxis
              type="number"
              tickFormatter={(value: number) => compactMoney(value)}
              tick={{ fontSize: 12, fill: '#64748B' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="code"
              width={56}
              tick={{ fontSize: 12, fill: '#1E293B', fontWeight: 600 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={{ fill: '#EEF2FF' }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const row = payload[0].payload as (typeof data)[number];
                return (
                  <Box
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      p: 1.25,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {money(row.value)} · {row.holders} holders
                    </Typography>
                  </Box>
                );
              }}
            />
            <Bar
              isAnimationActive={false}
              dataKey="value"
              fill="#4F46E5"
              radius={[0, 4, 4, 0]}
              barSize={18}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </SectionCard>
  );
}
